import React, { useCallback, useEffect } from 'react'
import {
  IconInfoCircle,
  IconCheckCircle,
  IconExclamationCircle,
  IconX,
  IconExclamation
} from '@paro.io/base-icons'
import { Button } from '../Button'
import { Heading } from '../Heading'
import classNames from 'classnames'
export enum ModalVariant {
  passive = 'passive',
  acknowledgement = 'acknowledgement',
  transactional = 'transactional'
}

interface ModalProps {
  children?: React.ReactNode
  className?: string
  color?: string
  icon?: string
  iconCustom?: React.ReactNode
  label?: string
  loading?: boolean
  open?: boolean
  onClose?: () => void
  onPrimaryButtonClickHandler?: () => void
  size?: string
  variant?: ModalVariant
}

const getIcon = (icon: string | undefined) => {
  if (!icon) {
    return ''
  }

  const iconColorConfig = {
    warning: 'text-warning',
    danger: 'text-danger',
    success: 'text-success'
  }

  const iconConfig = {
    info: <IconInfoCircle />,
    warning: <IconExclamationCircle />,
    danger: <IconExclamation />,
    success: <IconCheckCircle />
  }

  const iconComponent = iconConfig[icon]
  if (!iconComponent) {
    return ''
  }

  return <span className={iconColorConfig[icon]}>{iconComponent}</span>
}

const getFooter = (
  color?: string,
  clickHandler?: () => void,
  loading?: boolean,
  onClose?: () => void,
  variant?: ModalVariant
) => {
  switch (variant) {
    case ModalVariant.passive:
      return <div />
    case ModalVariant.acknowledgement:
      return (
        <Button
          label='OK'
          onClick={clickHandler}
          color={color}
          isLoading={loading}
        />
      )
    case ModalVariant.transactional:
      return (
        <TransactionalDialogFooter
          onPrimaryButtonClickHandler={clickHandler}
          color={color}
          onClose={onClose}
          loading={loading}
        />
      )
    default:
      return <div />
  }
}

const getModalSize = (size: string | undefined) => {
  switch (size) {
    case 'sm':
      return 'w-full sm:w-8/12 md:w-7/12 lg:w-6/12 xl:w-5/12 2xl:w-4/12'
    case 'md':
      return 'w-full sm:w-10/12 md:w-9/12 lg:w-8/12 xl:w-7/12 2xl:w-6/12'
    case 'lg':
      return 'w-full sm:w-12/12 md:w-11/12 lg:w-10/12 xl:w-9/12 2xl:w-8/12'
    default:
      return 'w-full sm:w-10/12 md:w-9/12 lg:w-8/12 xl:w-7/12 2xl:w-6/12'
  }
}

export const Modal = ({
  color,
  children,
  className = '',
  icon,
  iconCustom,
  label = '',
  loading,
  open,
  onClose,
  onPrimaryButtonClickHandler,
  size = 'md',
  variant
}: ModalProps) => {
  const handleEscapeClick = useCallback((event: any) => {
    if (event.key === 'Escape') {
      onClose && onClose()
    }
  }, [])

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscapeClick)
    } else {
      document.removeEventListener('keydown', handleEscapeClick)
    }
  }, [open, onClose])

  const onModalClick = (e: any) => {
    e.stopPropagation()
  }

  const iconOption = getIcon(icon)
  const modalSizeOption = getModalSize(size)

  return open ? (
    <div
      data-testid='modal'
      role='dialog'
      aria-modal='true'
      className={`fixed z-10 inset-0 overflow-y-auto ${className}`}
      onClick={onClose}
    >
      <div className='flex justify-center min-h-screen pt-2 px-1 pb-20 text-center items-center sm:p-0 bg-gray-900 bg-opacity-50'>
        <div
          className={classNames({
            [modalSizeOption]: true,
            'inline-block align-bottom bg-white rounded text-left overflow-hidden shadow-xl transform transition-all':
              true
          })}
          onClick={onModalClick}
        >
          <div className='bg-white p-4 sm:p-8'>
            <div className='sm:items-start sm:justify-center w-full'>
              <div className='flex flex-row-reverse md:block'>
                <div
                  className='cursor-pointer absolute right-4 top-4'
                  onClick={onClose}
                >
                  <IconX />
                </div>
                <div className='items-center flex justify-items-start inline-block w-full'>
                  {iconCustom ? (
                    <div className='mr-2'>{iconCustom}</div>
                  ) : (
                    <div className='mr-2'>{iconOption}</div>
                  )}
                  <Heading
                    size='h4'
                    className={classNames('p-0 text-xl align-middle')}
                  >
                    {label}
                  </Heading>
                </div>
              </div>
              <div className='w-full min-h-48 py-8'>{children}</div>
              {getFooter(
                color,
                onPrimaryButtonClickHandler,
                loading,
                onClose,
                variant
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null
}

export const TransactionalDialogFooter = (
  props: Pick<
    ModalProps,
    'color' | 'loading' | 'onClose' | 'onPrimaryButtonClickHandler'
  >
) => {
  const { color, loading, onClose, onPrimaryButtonClickHandler } = props
  return (
    <div>
      <Button label='Cancel' onClick={onClose} />
      <Button
        className='mx-3'
        color={color}
        label='Continue'
        onClick={onPrimaryButtonClickHandler}
        isLoading={loading}
      />
    </div>
  )
}
